import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';

const CompanyFormation = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Basic LLC", price: "$99", features: ["LLC Formation", "Articles of Organization", "EIN Registration", "Email Support", "Standard Processing"] },
    { name: "Standard LLC", price: "$249", popular: true, features: ["Everything in Basic", "Operating Agreement", "Attorney Review", "Priority Support", "Registered Agent Service", "Banking Resolution"] },
    { name: "Premium LLC", price: "$499", features: ["Everything in Standard", "Dedicated Attorney", "24/7 Premium Support", "Multi-State Filing", "Compliance Monitoring", "Annual Report Filing"] }
  ];

  const process = [
    { step: "01", title: "Business Planning", description: "Consult with our experts to choose the right business structure and state for your LLC formation." },
    { step: "02", title: "Filing & Formation", description: "We prepare and file all necessary documents with the state to officially form your LLC." },
    { step: "03", title: "Compliance & Setup", description: "Receive your LLC documents, EIN, and ongoing compliance support for your new business." }
  ];

  const testimonials = [
    { name: "Robert Davis", role: "Entrepreneur", text: "Formed my LLC quickly and professionally. Excellent service!" },
    { name: "Jennifer Lopez", role: "Consultant", text: "Easy process with great guidance. Highly recommend for LLC formation!" },
    { name: "Mark Anderson", role: "Real Estate Investor", text: "Professional team that made LLC formation stress-free." },
    { name: "Amy Chen", role: "E-commerce Owner", text: "Fast LLC setup with all necessary documents. Great value!" },
    { name: "Thomas Wright", role: "Freelancer", text: "Expert help from start to finish. My LLC was ready in days!" }
  ];

  const faqs = [
    { question: "What is an LLC?", answer: "A Limited Liability Company (LLC) is a business structure that provides personal liability protection while offering flexible tax treatment and simplified management." },
    { question: "How long does LLC formation take?", answer: "LLC formation typically takes 1-2 weeks, depending on the state's processing times. Expedited filing options are available." },
    { question: "Do I need an attorney to form an LLC?", answer: "While not required, having professional assistance ensures proper formation, compliance, and helps avoid costly mistakes." },
    { question: "What's included in LLC formation?", answer: "LLC formation includes filing Articles of Organization, obtaining an EIN, creating an Operating Agreement, and providing compliance guidance." },
    { question: "What are the ongoing requirements for an LLC?", answer: "LLCs must file annual reports, maintain good standing, comply with state regulations, and keep proper business records." }
  ];

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <Header />
      <div className="pt-20 pb-16 bg-gradient-to-b from-[#131921] to-[#0F1419]">
        <div className="container mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center text-[#FF9900] hover:text-orange-400 mb-6 transition-colors"><ArrowLeft className="w-4 h-4 mr-2"/>Back to Home</Link>
          <h1 className="text-5xl font-bold mb-6 text-[#FF9900]">Company Formation (LLC)</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Form your LLC with professional assistance. Protect your personal assets starting at $99.</p>
        </div>
      </div>
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1636819488537-a9b1ffb315ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzdGFydHVwJTIwYnVzaW5lc3MlMjBsYXVuY2glMjBjb21wYW55JTIwYnVpbGRpbmclMjB2ZWN0b3IlMjBpbGx1c3RyYXRpb258ZW58MHx8fHwxNzc1MDc3MzE0fDA&ixlib=rb-4.1.0&q=85" alt="LLC" className="w-full h-full object-cover"/>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">About LLC Formation</h2>
              <p className="text-gray-300 mb-4 leading-relaxed">An LLC provides personal liability protection, separating your personal assets from business debts. It offers flexible tax treatment and simplified management compared to corporations.</p>
              <p className="text-gray-300 mb-6 leading-relaxed">Our LLC formation services handle all the paperwork, state filings, and compliance requirements, making it easy to start your business with confidence.</p>
              <Button 
                onClick={() => setIsContactFormOpen(true)}
                className="bg-[#FF9900] hover:bg-orange-600 text-white"
              >Get Started Today</Button>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Our Simple 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item, i) => (
              <div key={i} className="bg-[#0F1419] p-8 rounded-2xl border border-white/10 hover:border-[#FF9900]/50 transition-all">
                <div className="text-6xl font-bold text-[#FF9900] mb-4 opacity-20">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Affordable LLC Formation Packages</h2>
          <p className="text-center text-gray-300 mb-16">Choose the package that fits your business needs</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`relative bg-[#131921] p-8 rounded-2xl border-2 ${plan.popular ? 'border-[#FF9900]' : 'border-white/10'} hover:border-[#FF9900]/50 transition-all`}>
                {plan.popular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2"><span className="bg-[#FF9900] text-white px-4 py-1 rounded-full text-sm font-bold">MOST POPULAR</span></div>}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-[#FF9900] mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, idx) => (
                    <li key={idx} className="flex items-start"><Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0"/><span className="text-gray-300">{f}</span></li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setIsContactFormOpen(true)}
                  className={`w-full ${plan.popular ? 'bg-[#FF9900] hover:bg-orange-600' : 'bg-white/10 hover:bg-white/20'} text-white`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Our LLC Services?</h2>
              <div className="space-y-4">
                {["Expert Business Formation", "Fast State Filing", "Affordable Packages", "Ongoing Support"].map((title, i) => (
                  <div key={i} className="flex items-start">
                    <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0"/>
                    <div><h3 className="text-xl font-bold mb-2">{title}</h3><p className="text-gray-300">Professional LLC formation with complete compliance guidance</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1676907820329-d74d048a6969?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMGdyb3d0aCUyMHN1Y2Nlc3MlMjBnZW9tZXRyaWMlMjAzZCUyMGlsbHVzdHJhdGlvbnxlbnwwfHx8fDE3NzUwNzczMjR8MA&ixlib=rb-4.1.0&q=85" alt="Why Choose" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-r from-[#FF9900] to-orange-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Form Your LLC?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Join thousands of entrepreneurs who have successfully formed their LLCs with our expert assistance.</p>
          <Button 
            onClick={() => setIsContactFormOpen(true)}
            className="bg-white text-[#FF9900] hover:bg-gray-100 text-lg px-8 py-6">Start Your LLC Formation<ArrowRight className="ml-2 w-5 h-5"/></Button>
        </div>
      </section>
      <ServiceTestimonials 
        testimonials={testimonials}
        serviceName="Company Formation"
      />
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#0F1419] p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold mb-3 text-[#FF9900]">{faq.question}</h3>
                <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      
      <ContactFormDialog 
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </div>
  );
};

export default CompanyFormation;
